import { applySnapshot, types } from 'mobx-state-tree';

import { rootStore } from 'models';

import {
    EMPTY_COMMON_CODE,
    EMPTY_DETAIL_CODE,
    EMPTY_ATTRIBUTE,
    ALL_OPTION,
} from 'pages/AuthMenu/CommonCodeSetting/constants';
import CompanyService from 'services/CompanyService';

export const cmCodesApi = {
    async getDetailCodes(payload) {
        try {
            const response = await CompanyService.getCommonDetailCodeTableSetting({ ...payload });
            return response?.data || [];
        } catch (error) {
            console.error('Error fetching detail codes:', error);
            return [];
        }
    },
    async getDetailCodesListSelected(payload) {
        try {
            const response = await CompanyService.getCommonDetailCodeListSelected(payload);
            return response?.data || [];
        } catch (error) {
            console.error('Error fetching detail codes:', error);
            return [];
        }
    },
    async saveDetailCodes(payload) {
        try {
            const response = await CompanyService.saveDetailCodeTableSetting(payload);
            return response;
        } catch (error) {
            console.error('Error fetching detail codes:', error);
            return error;
        }
    },
    async deleteDetailCodes() { },
};

/**
 * MobX-State-Tree model for Common Code Setting page
 * Manages state for 3 tables: CommonCodes, DetailCodes, AttributeAdditions
 */
export const CmCodes = types
    .model('CmCodes', {
        // ---- Filter params ----
        filterParams: types.optional(
            types.model('CmCodesFilterParams', {
                codeOrName: types.optional(types.string, ''),
                useYn: types.optional(types.string, ''),
            }),
            {},
        ),

        // ---- Table 1: Common Code List ----
        commonCodes: types.optional(types.array(types.frozen()), []),
        filteredCommonCodesData: types.optional(types.array(types.frozen()), []),
        deletedCommonCodes: types.optional(types.array(types.frozen()), []),

        // ---- Table 2: Detail Code List ----
        detailCodes: types.optional(types.array(types.frozen()), []),
        deletedDetailCodes: types.optional(types.array(types.frozen()), []),
        colsDetailEtc: types.optional(types.array(types.frozen()), []), // For dynamic columns in detail code table, if needed

        // ---- Table 3: Attribute Additions ----
        attributes: types.optional(types.array(types.frozen()), []),
        deletedAttributes: types.optional(types.array(types.frozen()), []),

        // ---- Selection ----
        selectedCommonCodeId: types.maybeNull(types.union(types.number, types.string)),
        selectedDetailCodeId: types.maybeNull(types.union(types.number, types.string)),

        // ---- Loading states ----
        isLoading: types.optional(types.boolean, false),
        isDetailLoading: types.optional(types.boolean, false),
        isAttrLoading: types.optional(types.boolean, false),
        isSearched: types.optional(types.boolean, false),

        // ---- open detail table ----
        openTable: types.optional(
            types.model('CmCodesOpenTableParams', {
                commonCode: types.optional(types.boolean, false),
                detailCode: types.optional(types.boolean, false),
                attribute: types.optional(types.boolean, false),
            }),
            {},
        ),

    })
    .views((self) => ({
        get colsDetailEtcData() {
            return [...self.colsDetailEtc];
        },
        get isUpdateCommonCodeList() {
            return self.commonCodes.some((row) => row.status) || self.deletedCommonCodes.length > 0;
        },
        get isUpdateDetailCodeList() {
            return self.detailCodes.some((row) => row.status) || self.deletedDetailCodes.length > 0;
        },
        get commonCodesData() {
            return [...self.commonCodes];
        },
        get detailCodesData() {
            return [...self.detailCodes];
        },
        get openTableData() {
            return { ...self.openTable };
        },
        get selectedCommonCodeIdData() {
            return self.selectedCommonCodeId;
        }
    }))
    .actions((self) => ({
        // ============================================================
        // General
        // ============================================================
        reset() {
            applySnapshot(self, {});
        },

        setLoading(value) {
            self.isLoading = value;
        },

        setDetailLoading(value) {
            self.isDetailLoading = value;
        },

        setAttrLoading(value) {
            self.isAttrLoading = value;
        },

        clearDeletedCommonCodes() {
            self.deletedCommonCodes.clear();
        },

        clearDeletedDetailCodes() {
            self.deletedDetailCodes.clear();
        },

        clearDeletedAttributes() {
            self.deletedAttributes.clear();
        },

        clearDetailColsEtc() {
            self.colsDetailEtc.clear();
        },

        setSearched(value) {
            self.isSearched = value;
        },

        setFilterSearchData(data) {
            self.filteredCommonCodesData.replace(data || []);
        },

        setColsDetailEtc(data) {
            self.colsDetailEtc.replace(data || []);
        },

        // ============================================================
        // Filter actions
        // ============================================================
        setFilterParams(params) {
            Object.keys(params).forEach((key) => {
                if (key in self.filterParams) {
                    self.filterParams[key] = params[key];
                }
            });
        },

        resetFilters() {
            self.filterParams.codeOrName = '';
            self.filterParams.useYn = '';
        },

        // ============================================================
        // Table 1: Common Code actions
        // ============================================================
        setCommonCodes(data) {
            self.commonCodes.replace(data || []);
        },
        updateCommonCode(id, field, value) {
            const index = self.commonCodes.findIndex((row) => row.id === id);
            if (index > -1) {
                const current = self.commonCodes[index];
                const updated = {
                    ...current,
                    [field]: value,
                    status: current.status === 'ADD' ? 'ADD' : 'UPD',
                };
                self.commonCodes.splice(index, 1, updated);
            }
        },

        addCommonCode() {
            const tempId = `temp-${Date.now()}`;
            self.commonCodes.push({
                ...EMPTY_COMMON_CODE,
                id: tempId,
                status: 'ADD',
                companyId: rootStore?.company.get.id || null,
            });
        },

        deleteCommonCodes(ids = []) {
            const deleteSet = new Set(ids);
            const toDelete = self.commonCodes
                .filter((row) => deleteSet.has(row.id))
                .map((row) => ({ ...row, status: 'DEL' }));

            // Track deletions for server sync
            const merged = [...self.deletedCommonCodes];
            toDelete.forEach((row) => {
                const existIdx = merged.findIndex((item) => item.id === row.id);
                if (existIdx > -1) merged[existIdx] = row;
                else merged.push(row);
            });
            self.deletedCommonCodes.replace(merged);

            const remaining = self.commonCodes.filter((row) => !deleteSet.has(row.id));
            self.commonCodes.replace(remaining);
        },

        setSelectedCommonCodeId(id) {
            self.selectedCommonCodeId = id;
        },

        searchDataCommonCodes() { },

        // ============================================================
        // Table 2: Detail Code actions
        // ============================================================
        setDetailCodes(data) {
            self.detailCodes.replace(data || []);
        },

        updateDetailCode(id, field, value) {
            const index = self.detailCodes.findIndex((row) => row.mstSerl === id);
            if (index > -1) {
                const current = self.detailCodes[index];
                const updated = {
                    ...current,
                    [field]: value,
                    status: current.status === 'ADD' ? 'ADD' : 'UPD',
                };
                self.detailCodes.splice(index, 1, updated);
            }
        },

        addDetailCode() {
            if (!self.selectedCommonCodeId) return;
            const tempId = `temp-${Date.now()}`;
            self.detailCodes.push({
                ...EMPTY_DETAIL_CODE,
                mstSerl: tempId,
                mstId: self.selectedCommonCodeId,
                status: 'ADD',
                companyId: rootStore?.company.get.id || null,
            });
        },

        deleteDetailCodes(ids = []) {
            const deleteSet = new Set(ids);
            const toDelete = self.detailCodes
                .filter((row) => deleteSet.has(row.mstSerl))
                .map((row) => ({ ...row, status: 'DEL' }));

            const merged = [...self.deletedDetailCodes];
            toDelete.forEach((row) => {
                const existIdx = merged.findIndex((item) => item.mstSerl === row.mstSerl);
                if (existIdx > -1) merged[existIdx] = row;
                else merged.push(row);
            });

            self.deletedDetailCodes.replace(merged);

            const remaining = self.detailCodes.filter((row) => !deleteSet.has(row.mstSerl));
            self.detailCodes.replace(remaining);
        },
        getColsDetailCodeTableSetting: async (mstId) => {
            try {
                if (!mstId) {
                    self.setColsDetailEtc([]);
                    return [];
                }
                const data = await CompanyService.getColsDetailCodeTableSetting({ mstId });
                self.setColsDetailEtc(data?.data);
                return data;
            } catch (error) {
                console.error('Failed to fetch detail code table settings:', error);
                throw error;
            }
        },
        setSelectedDetailCodeId: (id) => {
            self.selectedDetailCodeId = id;
        },
        // ============================================================
        // Table 3: Attribute Additions actions
        // ============================================================
        setAttributes(data) {
            self.attributes.replace(data || []);
        },

        updateAttribute(id, field, value) {
            const index = self.attributes.findIndex((row) => row.id === id);
            if (index > -1) {
                const current = self.attributes[index];
                const updated = {
                    ...current,
                    [field]: value,
                    status: current.status === 'ADD' ? 'ADD' : 'UPD',
                };
                self.attributes.splice(index, 1, updated);
            }
        },

        addAttribute() {
            if (!self.selectedCommonCodeId) return;
            const id = `id-${Math.random().toString().slice(2, 9)}`;
            self.attributes.push({ ...EMPTY_ATTRIBUTE, id, status: 'ADD' });
        },

        deleteAttributes(ids = []) {
            const deleteSet = new Set(ids);
            const attributesToDelete = self.attributes.filter((row) => deleteSet.has(row.id));
            const toDelete = attributesToDelete
                .filter((row) => !!row.createdAt)
                .filter((row) => deleteSet.has(row.id))
                .map((row) => ({ ...row, status: 'DEL' }));

            const merged = [...self.deletedAttributes];
            toDelete.forEach((row) => {
                const existIdx = merged.findIndex((item) => item.id === row.id);
                if (existIdx > -1) merged[existIdx] = row;
                else merged.push(row);
            });
            self.deletedAttributes.replace(merged);

            const remaining = self.attributes.filter((row) => !deleteSet.has(row.id));
            self.setAttributes(remaining);
            self.attributes.replace(remaining);
        },
        getCommonCodeTableSetting: async () => {
            try {
                const { codeOrName, useYn } = self.filterParams;
                const data = await self.fetchAllPages(
                    (payload) =>
                        CompanyService.getCommonCodeTableSetting({
                            ...payload,
                            ...(codeOrName ? { commCodeName: codeOrName } : {}),
                            ...(useYn && useYn !== ALL_OPTION ? { useYn: useYn === 'Y' } : {}),
                        }),
                    500,
                );
                self.setCommonCodes(data || []);

                self.selectCommonCode(data?.[0]?.id || ''); // auto-select first common code if exists
                return data;
            } catch (error) {
                console.error('Failed to fetch common code table setting:', error);
                throw error;
            }
        },
        // ============================================================
        // API: handle search/filter — refetch common codes with current filters
        // ============================================================
        async filterCmCodes(draftFilterParams) {
            self.setFilterParams(draftFilterParams);
            self.setDetailCodes([]);
            self.setAttributes([]);
            self.setSelectedCommonCodeId('');
            self.setSelectedDetailCodeId('');
            self.clearDeletedCommonCodes();
            self.clearDeletedDetailCodes();
            self.clearDeletedAttributes();
            self.clearDetailColsEtc();
            self.getCommonCodeTableSetting();
        },
        fetchDetailCodesList: async (payload) => {
            try {
                if (!payload?.mstId) {
                    self.setDetailCodes([]);
                    return [];
                }
                const data = await cmCodesApi.getDetailCodes(payload);
                self.setSelectedCommonCodeId(payload.mstId);
                self.setDetailCodes(data || []);
                return data;
            } catch (error) {
                console.error('Failed to fetch detail codes:', error);
                throw error;
            }
        },
        fetchAllPages: async (apiFn, pageSize) => {
            const first = await apiFn({ page: 1, size: pageSize });

            const totalPages = Math.ceil(first.count / pageSize);

            const promises = [];

            for (let i = 2; i <= totalPages; i++) {
                promises.push(apiFn({ page: i, size: pageSize }));
            }

            const rest = await Promise.all(promises);

            const allData = [...first.data, ...rest.flatMap((r) => r.data)];

            return allData;
        },

        // ============================================================
        // API: Initialize — fetch common codes
        // ============================================================
        async initialize() {
            self.setLoading(true);
            try {
                await self.getCommonCodeTableSetting();
            } catch (error) {
                console.error('Failed to fetch common codes:', error);
                self.setCommonCodes([]);
            } finally {
                self.setLoading(false);
            }
        },

        // ============================================================
        // API: Save common codes
        // ============================================================
        async saveCommonCodes() {
            self.setLoading(true);
            try {
                const payload = {
                    upsert: self.commonCodes
                        .filter((row) => row.status === 'ADD' || row.status === 'UPD')
                        .map((row) => ({
                            ...row,
                            id: typeof row.id === 'string' && row.id.startsWith('temp-') ? null : row.id,
                            useYn: typeof row.useYn === 'string' ? row.useYn === 'Y' : row.useYn,
                        })),
                    delete:
                        self.deletedCommonCodes.length > 0
                            ? self.deletedCommonCodes
                                .map((row) => ({ ...row, useYn: row.useYn === 'Y' }))
                                .filter((row) => typeof row.id === 'number')
                            : [],
                };

                const isValid = payload.upsert.every((row) => row.commCodeType && row.commCodeName);

                if (!isValid) {
                    console.error('Invalid common code data');
                    return false;
                }

                const res = await CompanyService.saveCommonCodeTableSetting([
                    ...payload.upsert,
                    ...payload.delete,
                ]);

                self.getCommonCodeTableSetting();
                self.clearDeletedCommonCodes();
                return res;
            } catch (error) {
                console.error('Failed to save common codes:', error);
                return error;
            } finally {
                self.setLoading(false);
            }
        },

        // ============================================================
        // API: Fetch detail codes for a common code
        // ============================================================
        async fetchDetailCodes(commonCodeId) {
            self.setDetailLoading(true);
            self.setSelectedCommonCodeId(commonCodeId);
            try {
                const data = await cmCodesApi.getDetailCodes(commonCodeId);

                self.setDetailCodes(data);
            } catch (error) {
                console.error('Failed to fetch detail codes:', error);
                self.setDetailCodes([]);
            } finally {
                self.setDetailLoading(false);
            }
        },

        // ============================================================
        // API: Save detail codes
        // ============================================================
        async saveDetailCodes() {
            if (!self.selectedCommonCodeId) return false;
            self.setDetailLoading(true);
            try {
                const payload = {
                    upsert: self.detailCodes
                        .filter((row) => row.status === 'ADD' || row.status === 'UPD')
                        .map((row) => ({
                            ...row,
                            mstSerl:
                                typeof row.mstSerl === 'string' && row.mstSerl.startsWith('temp-')
                                    ? null
                                    : row.mstSerl,
                        })),
                    delete:
                        self.deletedDetailCodes.length > 0
                            ? self.deletedDetailCodes
                                .map((row) => ({ ...row }))
                                .filter((row) => typeof row.mstSerl === 'number')
                            : [],
                };

                const dataSave = [...payload.upsert, ...payload.delete].map((row) => {
                    const { id, ...rest } = row;
                    return {
                        ...rest,
                    };
                });
                const data = await cmCodesApi.saveDetailCodes(dataSave);

                self.clearDeletedDetailCodes();
                return data;
            } catch (error) {
                console.error('Failed to save detail codes:', error);
                return error;
            } finally {
                self.setDetailLoading(false);
            }
        },

        // ============================================================
        // API: Delete detail codes
        // ============================================================
        async deleteDetailCodesApi(ids) {
            if (!self.selectedCommonCodeId) return false;
            try {
                await cmCodesApi.deleteDetailCodes(ids);
                return true;
            } catch (error) {
                console.error('Failed to delete detail codes:', error);
                return false;
            }
        },

        // ============================================================
        // API: Fetch attributes for a common code
        // ============================================================
        async fetchAttributes() {
            self.setAttrLoading(true);
            try {
                const data = await CompanyService.getCmAdditionItems(self.selectedCommonCodeId);
                const dataWithIds = data?.data?.map((item) => ({
                    ...item,
                    id: `id-${Math.random().toString().slice(2, 9)}`,
                }));
                self.setAttributes(dataWithIds);
            } catch (error) {
                console.error('Failed to fetch attributes:', error);
                self.setAttributes([]);
            } finally {
                self.setAttrLoading(false);
            }
        },

        // ============================================================
        // API: Save attributes
        // ============================================================
        async saveAttributes() {
            self.setAttrLoading(true);
            try {
                const companyId = rootStore?.company.get.id;
                const payload = [...self.attributes, ...self.deletedAttributes]
                    .filter((row) => row.status)
                    .map((row) => ({
                        ...row,
                        companyId,
                        mstId: self.selectedCommonCodeId,
                        // etcSerl: self.selectedDetailCodeId,
                        etcInputTypeCd: row.etcInputType !== 'CODE' ? null : row.etcInputTypeCd,
                        // id: typeof row.id === 'string' && row.id.startsWith('temp-') ? null : row.id,
                    }));

                const res = await CompanyService.saveCmAdditionItems(payload);

                self.fetchAttributes();
                self.clearDeletedAttributes();
                if (self.selectedCommonCodeId) {
                    self.getColsDetailCodeTableSetting(self.selectedCommonCodeId); // refresh column list of table 2 Detail Codes
                }
                return res?.result;
            } catch (error) {
                console.error('Failed to save attributes:', error);
                throw error;
            } finally {
                self.setAttrLoading(false);
            }
        },

        // ============================================================
        // API: Select a common code row → load detail + attr in parallel
        // ============================================================
        async selectCommonCode(commonCodeId = '') {
            try {
                self.setSelectedCommonCodeId(commonCodeId);
                self.clearDeletedDetailCodes();
                self.clearDeletedAttributes();
                self.clearDetailColsEtc();
                if (commonCodeId) {
                    await Promise.all([
                        self.fetchDetailCodesList({ mstId: commonCodeId }),
                        self.fetchAttributes(commonCodeId),
                    ]);
                    await self.getColsDetailCodeTableSetting(commonCodeId);
                }
            } catch (error) {
                console.error('Failed to load data for selected common code:', error);
            }
        },

        // open detail table
        setOpenTable(tableName, value) {
            if (tableName in self.openTable) {
                self.openTable[tableName] = value;
            }
        },
        clearOpenTable() {
            self.openTable.commonCode = false;
            self.openTable.detailCode = false;
            self.openTable.attribute = false;
        },
    }));
